import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const BlogCreator = ({ prompt, data }) => {
  const { FiCopy, FiCheck, FiExternalLink, FiEdit, FiHome, FiImage, FiVideo } = FiIcons;
  const [copied, setCopied] = useState(false);
  const [showLumadiousModal, setShowLumadiousModal] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('BlogCreator received prompt:', prompt);
    console.log('BlogCreator received data:', data);
  }, [prompt, data]);

  const generateBlogPost = () => {
    // Provide fallback data if none exists
    const safeData = data || {
      location: 'Santorini',
      vibe: 'Cultural Immersion',
      travelerType: 'Solo',
      ageGroup: 'Adult (25-64)',
      gender: 'Non-binary',
      lgbtqIdentity: 'Ally',
      mobility: 'None',
      accessibility: 'Standard Access',
      experience: 'Local Market Exploration',
      pose: 'Engaged with local culture',
      culturalContext: 'Respectful Tourism'
    };

    const safePrompt = prompt || "Experience inclusive travel through the lens of diverse travelers, captured confidently at stunning destinations worldwide. This journey embodies the spirit of cultural immersion and authentic representation for all.";

    const title = `Discovering ${safeData.location || 'Global Destinations'}: A ${safeData.vibe || 'Cultural'} Experience for All Travelers`;
    
    const introduction = `There's something magical about ${safeData.location || 'travel'} that speaks to the soul of every traveler. As part of the global travel community, I'm here to share why this destination deserves a spot on everyone's bucket list, regardless of age, ability, gender identity, or background.`;
    
    const mainContent = safePrompt;
    
    const accessibility = safeData.mobility !== 'None' || safeData.accessibility !== 'Standard Access' 
      ? `\n\n**Accessibility & Inclusion**\nThis destination welcomes travelers with diverse needs, offering ${safeData.mobility?.toLowerCase() || 'standard'} accessibility options and ${safeData.accessibility?.toLowerCase() || 'inclusive'} experiences that ensure everyone can enjoy the beauty and culture without barriers.` 
      : `\n\n**Universal Accessibility**\nThis destination celebrates diversity and welcomes all travelers, creating an environment where everyone feels represented and valued.`;

    const lgbtqSection = safeData.lgbtqIdentity !== 'Ally' 
      ? `\n\n**LGBTQ+ Friendly Destination**\nAs ${safeData.lgbtqIdentity.toLowerCase()}, finding welcoming spaces is essential. ${safeData.location} offers inclusive accommodations, safe social venues, and a community that celebrates diversity in all forms.` 
      : '';

    const ageSection = safeData.ageGroup !== 'Adult (25-64)' 
      ? `\n\n**${safeData.ageGroup} Travel Focus**\nThis ${safeData.vibe?.toLowerCase() || 'cultural'} experience is perfectly suited for ${safeData.ageGroup.toLowerCase()} travelers, with appropriate pace, activities, and accommodations that cater to this specific age demographic.` 
      : '';

    const travelerSection = `\n\n**Perfect for ${safeData.travelerType}**\nThe ${safeData.vibe?.toLowerCase() || 'immersive'} atmosphere provides the perfect backdrop for ${safeData.travelerType.toLowerCase()} to create lasting memories and authentic connections.`;

    const conclusion = `\n\n**Ready to Experience ${safeData.location || 'This Destination'}?**\nYour ${safeData.vibe?.toLowerCase() || 'cultural'} adventure awaits. From ${safeData.experience?.toLowerCase() || 'cultural exploration'} to every sunset moment, this destination promises memories that will last a lifetime for travelers of all backgrounds and abilities.\n\n*What's your next dream destination? Share in the comments below!*`;

    return {
      title,
      content: `${introduction}\n\n${mainContent}${accessibility}${lgbtqSection}${ageSection}${travelerSection}${conclusion}`,
      tags: [
        `#${safeData.location?.replace(/\s+/g, '') || 'Travel'}`,
        '#InclusiveTravel',
        '#AccessibleTravel',
        '#LGBTQTravel',
        '#SeniorTravel',
        '#GlobalTravel',
        `#${safeData.vibe?.replace(/\s+/g, '') || 'CulturalTravel'}`,
        '#TravelForAll'
      ]
    };
  };

  const blogPost = generateBlogPost();

  const copyBlogPost = async () => {
    const fullBlog = `${blogPost.title}\n\n${blogPost.content}\n\n${blogPost.tags.join(' ')}`;
    try {
      await navigator.clipboard.writeText(fullBlog);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy blog post: ', err);
      // Fallback for environments where clipboard API is not available
      const textArea = document.createElement('textarea');
      textArea.value = fullBlog;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openArtistly = () => {
    window.open('https://artistly.ai', '_blank');
  };

  const openArtistlyDashboard = () => {
    window.open('https://app.artistly.ai', '_blank');
  };

  const openVideoExpress = () => {
    window.open('https://paykstrt.com/50942/164495', '_blank');
  };

  const openVideoExpressDashboard = () => {
    window.open('https://videoexpress.ai/dashboard', '_blank');
  };

  const handleLumadiousAccess = (hasAccount) => {
    if (hasAccount) {
      window.open('https://app.lumadious.us', '_blank');
    } else {
      window.open('https://link.fastpaydirect.com/payment-link/68793769ddc6a689bcc555a9', '_blank');
    }
    setShowLumadiousModal(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-yellow-400">Inclusive Travel Blog Creator</h2>
          <div className="flex items-center space-x-3">
            <button 
              onClick={copyBlogPost} 
              className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              <span>{copied ? 'Copied!' : 'Copy Blog'}</span>
            </button>
          </div>
        </div>

        {/* Blog Preview */}
        <div className="bg-white text-black rounded-lg p-8 mb-6">
          <h1 className="text-3xl font-bold mb-6 text-gray-900">{blogPost.title}</h1>
          <div className="prose prose-lg max-w-none">
            <div className="whitespace-pre-line leading-relaxed text-gray-700">
              {blogPost.content}
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {blogPost.tags.map((tag, index) => (
                <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Content Enhancement Tools */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <SafeIcon icon={FiImage} className="mr-2 text-purple-400" />
              Generate Images with Artistly
            </h3>
            <p className="text-gray-300 mb-4">
              Create stunning, inclusive travel images that match your blog content using AI.
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={openArtistly} 
                className="bg-purple-500 hover:bg-purple-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <SafeIcon icon={FiExternalLink} />
                <span>Get Artistly</span>
              </button>
              <button 
                onClick={openArtistlyDashboard} 
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <SafeIcon icon={FiExternalLink} />
                <span>Login Dashboard</span>
              </button>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <SafeIcon icon={FiVideo} className="mr-2 text-blue-400" />
              Create Videos with VideoExpress
            </h3>
            <p className="text-gray-300 mb-4">
              Turn your blog content into engaging video stories with AI-powered VideoExpress.
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={openVideoExpress} 
                className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <SafeIcon icon={FiExternalLink} />
                <span>Get VideoExpress</span>
              </button>
              <button 
                onClick={openVideoExpressDashboard} 
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <SafeIcon icon={FiExternalLink} />
                <span>Login Dashboard</span>
              </button>
            </div>
          </div>
        </div>

        <div className="bg-gray-700 rounded-lg p-6 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <SafeIcon icon={FiEdit} className="mr-2 text-yellow-400" />
            Publish with Clarity with Jahx
          </h3>
          <p className="text-gray-300 mb-4">
            Take your blog to the next level with Clarity with Jahx's all-in-one platform for inclusive travel content creators.
          </p>
          <button 
            onClick={() => setShowLumadiousModal(true)} 
            className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <SafeIcon icon={FiExternalLink} />
            <span>Access Clarity with Jahx</span>
          </button>
        </div>

        {/* Blog Writing Tips */}
        <div className="bg-gray-700 rounded-lg p-6 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">Inclusive Blog Enhancement Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-300 mb-2">Content Structure</h4>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• Start with a compelling hook</li>
                <li>• Include diverse traveler perspectives</li>
                <li>• Add accessibility information</li>
                <li>• Use inclusive language throughout</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-300 mb-2">SEO & Inclusivity</h4>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• Use diverse representation keywords</li>
                <li>• Include accessibility terms</li>
                <li>• Add LGBTQ+ friendly destination info</li>
                <li>• Include age-appropriate details</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center space-x-4">
          <button 
            onClick={() => window.location.href = '#/'} 
            className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <SafeIcon icon={FiHome} />
            <span>Back to Dashboard</span>
          </button>
          <button 
            onClick={() => window.location.href = '#/generator'} 
            className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <SafeIcon icon={FiEdit} />
            <span>Edit Prompt</span>
          </button>
        </div>
      </div>

      {/* Lumadious Access Modal */}
      {showLumadiousModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-white mb-4">Access Clarity with Jahx Platform</h3>
            <p className="text-gray-300 mb-6">
              Do you already have a Clarity with Jahx account, or do you need to sign up for our all-in-one inclusive travel content platform?
            </p>
            <div className="flex space-x-4">
              <button 
                onClick={() => handleLumadiousAccess(true)} 
                className="bg-green-500 hover:bg-green-400 text-white px-6 py-3 rounded-lg flex-1 transition-colors"
              >
                I Have an Account
              </button>
              <button 
                onClick={() => handleLumadiousAccess(false)} 
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-3 rounded-lg flex-1 transition-colors"
              >
                Sign Me Up!
              </button>
            </div>
            <button 
              onClick={() => setShowLumadiousModal(false)} 
              className="w-full mt-4 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogCreator;